import { injectIntl, InjectedIntlProps } from "react-intl"
import * as React from "react"
import * as Utils from "./utils"
import { bodyW } from "../../pptx-configuration/constants"
import * as CommonUtils from "../../utils"
import * as Conf from "../../../configuration"

class OperationTable
extends React.Component<Props & InjectedIntlProps, State> {
	private readonly columns: Column[]
	private readonly evoCellWidth: number
	private readonly labelCellWidth: number
	private readonly numberCellWidth: number
	private readonly yearCellWidth: number

	constructor(props: Readonly<Props & InjectedIntlProps>) {
		super(props)

		this.columns = createColumns(props.years)
		this.labelCellWidth = CommonUtils.toInches(3)
		this.numberCellWidth = CommonUtils.toInches(0.7)
		const remainingSpace = bodyW - this.numberCellWidth - this.labelCellWidth

		/** Nombre de cellules d'années */
		const yearCellsCount = (this.columns.length + 1) / 2
		// 2/3 de l'espace restant
		this.yearCellWidth = (remainingSpace * 2 / 3) / yearCellsCount

		/** Nombre de cellule d'évolution */
		const evoCellsCount = yearCellsCount - 1
		// 1/3 de l'espace restant
		this.evoCellWidth = (remainingSpace / 3) / evoCellsCount
	}

	render() {
		const data = this.props.data
		const tableConf = this.props.tableConfiguration
		const columns = this.columns

		return <table id={this.props.id}>
			{/* En-tête */}
			<thead>
				<tr>
					<th data-pptx-width={this.numberCellWidth}
					style={{ ...tableConf.header }}></th>

					<th data-pptx-width={this.labelCellWidth}
					style={{ ...tableConf.header }}></th>

					{columns.map((column, colIndex) => {
						if (isEvolutionColumn(column)) {
							return <th
							data-pptx-width={this.evoCellWidth}
							key={colIndex}
							style={{ ...tableConf.header }}>
								Evol
							</th>
						}

						return <th
						data-pptx-width={this.yearCellWidth}
						key={colIndex}
						style={{ ...tableConf.header }}>
							{column}
						</th>
					})}
				</tr>
			</thead>

			<tbody>
				{/* Chapitres d'opérations réelles */}
				{data.realOperations.rows.map((row, rowIndex) =>
					<tr key={rowIndex}>
						<td style={{ ...tableConf.common }}>
							{row.chapter && row.chapter.number}
						</td>

						<td style={{ ...tableConf.common }}>
							{row.chapter ? row.chapter.label : "Non chapitré"}
						</td>

						{columns.map((c, i) =>
							isEvolutionColumn(c)
								? this.renderEvolutionCell(row, c, i)
								: this.renderValueCell(row, c, i)
						)}
					</tr>
				)}

				{/* Sous-total des opérations réelles */}
				<tr>
					<td style={{ ...tableConf.subtotal }}></td>

					<td style={{ ...tableConf.subtotal }}>
						{data.realOperations.label}
					</td>

					{columns.map((c, i) =>
						isEvolutionColumn(c)
							? this.renderEvolutionCell(data.realOperations.total, c, i, "subtotal")
							: this.renderValueCell(data.realOperations.total, c, i, "subtotal"))}
				</tr>

				{/* Chapitres des opérations d'ordre */}
				{data.orderOperations.rows.map((row, rowIndex) =>
					<tr key={rowIndex}>
						<td style={{...tableConf.common}}>
							{row.chapter && row.chapter.number}
						</td>

						<td style={{ ...tableConf.common }}>
							{row.chapter ? row.chapter.label : "Non chapitré"}
						</td>

						{columns.map((c, i) =>
						isEvolutionColumn(c)
							? this.renderEvolutionCell(row, c, i)
							: this.renderValueCell(row, c, i))}
					</tr>
				)}

				{/* Sous-total des opérations d'ordre */}
				<tr>
					<td style={{ ...tableConf.subtotal }}></td>

					<td style={{ ...tableConf.subtotal }}>
						{data.orderOperations.label}
					</td>

					{columns.map((c, i) =>
						isEvolutionColumn(c)
							? this.renderEvolutionCell(data.orderOperations.total, c, i, "subtotal")
							: this.renderValueCell(data.orderOperations.total, c, i, "subtotal"))}
				</tr>
			</tbody>

			{/* Total */}
			<tfoot>
				<tr>
					<th style={{ ...tableConf.total }}></th>

					<th style={{ ...tableConf.total }}>
						{data.total.label}
					</th>

					{columns.map((c, i) =>
						isEvolutionColumn(c)
							? this.renderEvolutionCell(data.total, c, i, "total")
							: this.renderValueCell(data.total, c, i, "total"))}
				</tr>
			</tfoot>
		</table>
	}

	private renderCell(
		value: string,
		key: number,
		negative: boolean,
		type: CellType
	): React.ReactNode {
		const tableConf = this.props.tableConfiguration
		let style: React.CSSProperties =
			type === "subtotal" ? { ...tableConf.subtotal, ...tableConf.numeric } :
			type === "total" ? { ...tableConf.total, ...tableConf.numeric } :
			{ ...tableConf.common, ...tableConf.numeric }

		if (negative) {
			style = { ...style, ...tableConf.negative }
		}

		return <td key={key} style={style}>{value}</td>
	}

	private renderEvolutionCell(
		row: Utils.ValuesWithEvolutions,
		column: EvolutionColumn,
		key: number,
		type?: CellType
	): React.ReactNode {
		const value = Utils.findEvolution(row.evolutions, column.prevYear).value
		const displayValue = this.props.intl
			.formatNumber(value, { style: "percent" })

		return this.renderCell(displayValue, key, value < 0, type)
	}

	private renderValueCell(
		row: Utils.ValuesWithEvolutions,
		year: number,
		key: number,
		type?: CellType
	): React.ReactNode {
		const value = row.values[year]
		const displayValue = CommonUtils.formatCurrency(
			this.props.locale, this.props.currency, value, this.props.roundness)

		return this.renderCell(displayValue, key, value < 0, type)
	}
}

export default injectIntl(OperationTable)

export interface Props {
	currency: string
	data: Utils.Data
	id: string
	locale: string
	roundness: Conf.RoundnessValue
	tableConfiguration: Conf.TableConfiguration
	years: number[]
}

function createColumns(years: number[]): Column[] {
	return years.reduce<Column[]>((columns, year, yearIndex) => {
		columns.push(year)

		if (yearIndex !== years.length - 1)
			columns.push({ nextYear: years[yearIndex + 1], prevYear: year })

		return columns
	}, [])
}

function isEvolutionColumn(column: Column): column is EvolutionColumn {
	return undefined !== (column as EvolutionColumn).prevYear
}

interface EvolutionColumn {
	nextYear: number
	prevYear: number
}

interface State {}

type CellType = "subtotal" | "total"
type Column = number | EvolutionColumn



// WEBPACK FOOTER //
// ./src/component/management-report/generator/observation-generator/operation-generator/operation-table.tsx